/**
 * What a stored answer is when a widget reads it, and how an option-backed
 * field validates one.
 *
 * A leaf on purpose: this imports zod and two types, and nothing else. Both
 * halves of the widget registry have to coerce the same stored values the
 * same way, and a widget that owns its descriptor in its own module — the
 * level ladder — has to build its schema without importing the registry it
 * is a member of. Either of those reaching into `widgets.ts` would close a
 * runtime import cycle; reaching in here cannot.
 */
import { z } from 'zod'
import type { AuditionAnswer, AuditionField } from './steps.js'
import type { StoredSchema } from './widgets.js'

/** A stored answer as the string a single-value widget requires. */
export function asText(value: AuditionAnswer | undefined): string {
  return typeof value === 'string' ? value : ''
}

/**
 * A stored answer as the list a multi-value widget requires.
 *
 * A lone non-empty string counts as a list of one, because every multi-pick
 * question here was a single chip group first and a draft saved then holds
 * the bare string. Dropping it would leave a returning applicant looking at
 * their own answer unselected, and then submit the empty list they never
 * chose.
 */
export function asList(value: AuditionAnswer | undefined): string[] {
  if (Array.isArray(value)) return value
  return typeof value === 'string' && value !== '' ? [value] : []
}

/** Free text, the shape every plain input stores. */
export function textSchema(field: AuditionField): StoredSchema {
  // One expression per shape, no `let` and no assertion: a widening
  // annotation over both paths is what forced an `as z.ZodString` to get
  // `.min` back, and that assertion would keep compiling if the initializer
  // ever gained a `.transform()` — throwing `value.min is not a function` out
  // of a function that runs on every step render and inside saveStep.
  const text = z.string().trim().max(5000)
  return field.required
    ? text.min(1)
    : text
        .or(z.literal(''))
        .optional()
        .transform((v) => v ?? '')
}

/**
 * The declared options as a tuple zod will take, or a loud failure.
 *
 * Only an EMPTY list can reach the throw: the field type owning options
 * declares them as required, so omitting them is a compile error at the
 * `QUESTION_STEPS` literal rather than a 500 from inside `stepSchema`.
 */
export function optionsOf(field: AuditionField): [string, ...string[]] {
  if (!field.options?.length)
    throw new Error(`${field.type} field "${field.name}" must declare options`)
  // Copied because `options` is readonly and z.enum wants a mutable tuple.
  // Safe: guarded non-empty immediately above.
  return [...field.options] as [string, ...string[]]
}

/**
 * A pick from a declared list, one answer or many.
 *
 * `list` is passed rather than looked up, because the descriptor that calls
 * this is the same one that answers `list` for its own field type — asking
 * the registry from here would mean this module importing it.
 */
export function optionSchema(
  field: AuditionField,
  list: boolean,
): StoredSchema {
  const options = optionsOf(field)
  if (list) {
    // Optional multi fields normalize a missing key to [] so the stored shape
    // never varies by whether the applicant touched the group.
    return field.required
      ? z.array(z.enum(options)).min(1)
      : z
          .array(z.enum(options))
          .optional()
          .transform((v) => v ?? [])
  }
  return field.required
    ? z.enum(options)
    : z
        .enum(options)
        .or(z.literal(''))
        .optional()
        .transform((v) => v ?? '')
}
